import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FeatureGroup, Map, Marker, divIcon, latLng, marker, tileLayer } from 'leaflet';
import { clone, omit } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { FlagService } from '../../flag/flag.service';
import { ImgAjaxComponent } from '../../shared/_components/img-ajax/img-ajax.component';
import { AnswerChange, Flag } from '../../shared/interfaces';
import { AddFlagComponent } from '../add.flag/add.flag.component';
import { AnswerService } from '../answer.service';
import { EditFlagComponent } from '../edit.flag/edit.flag.component';
import { PictureViewerComponent } from '../picture-viewer/picture-viewer.component';
import { PictureService } from '../picture.service';
import { getPictureDisplayTimestamp } from '../picture-display.utils';
import { UploadImageComponent } from '../upload.image/upload.image.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-answer-pictures',
  templateUrl: './answer-pictures.component.html',
  styleUrls: ['./answer-pictures.component.scss'],
})
export class AnswerPicturesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private queryParamsSub: Subscription;

  constructor(
    private toastr: ToastrService,
    public flagService: FlagService,
    public answerService: AnswerService,
    public pictureService: PictureService,
    public zone: NgZone,
    public dialog: DialogService,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
  ) {
    this.setFlexValues();
    this.addFlagSubject.pipe(debounceTime(250)).subscribe(coords => {
      this.placeholderFlag = coords;
      this.actualAddFlag(coords);
    });
  }

  private addFlagSubject = new Subject<any>();

  public get currentPicture() {
    return this._currentPicture;
  }
  public set currentPicture(value) {
    this._currentPicture = value;
    this.makeCurrentThumbVisible();
    this.updateCurrentFlags();
    this.centerMap();
  }

  @ViewChild('uploadImageModal', { static: false }) uploadImageModal: UploadImageComponent;

  @Input() answer;
  @Input() navigatedByArrow: 'left' | 'right';
  showRelated = true;
  showMapViewer = true;
  placeholderFlag;

  get pictures() {
    let all = [];

    if (Array.isArray(this.answer)) {
      all = this.answer.reduce((acc, curr) => [...acc, ...(curr.pictures || [])], []);
    } else {
      all = (this.answer && this.answer.pictures) || [];
    }

    if (this.showRelated) {
      return all;
    } else {
      return all.filter(p => !p.related);
    }
  }

  private _currentPicture;

  flagsWithTooltip = [];
  showFlags = false;
  updateImage = 0;
  iconStyle = '0';
  flexValue = '7';
  rightColumnFlexValue = '3';

  @Output() close = new EventEmitter();
  @Output() answerChange = new EventEmitter();
  @Output() updateCounter = new EventEmitter<void>();
  @Output() flagChange = new EventEmitter();

  @ViewChildren('thumb') thumbs: QueryList<ImgAjaxComponent>;
  @ViewChildren('mainimg') mainimg: QueryList<ImgAjaxComponent>;
  @ViewChildren('image') image: QueryList<ImgAjaxComponent>;
  @ViewChildren('imagefull') imagefull: QueryList<ElementRef>;
  @ViewChildren('imagesmall') imagesmall: QueryList<ElementRef>;
  @ViewChildren('viewerSmall') viewerSmall: QueryList<PictureViewerComponent>;
  @ViewChildren('viewerFull') viewerFull: QueryList<PictureViewerComponent>;
  @ViewChild('addFlagModal', { static: false }) addFlagModal: AddFlagComponent;
  @ViewChild('editFlagModal', { static: false }) editFlagModal: EditFlagComponent;
  @ViewChild('thumbsContainer', { static: false }) thumbsContainer: ElementRef;

  fullScreen = false;
  private bodyExitButton: HTMLElement | null = null;
  private bodyExitUnlisten: (() => void) | undefined;
  map: Map = null;
  lOptions = {} as any;
  lLayers: any[] = [];
  markers = new FeatureGroup();

  currentFlags: any[];

  zoom = false;

  ngAfterViewInit() {
    if (this.currentPicture === this.pictures.length - 1) {
      this.fixScrollPosition();
    }
  }

  addFlag(coords: any) {
    this.placeholderFlag = coords;
    this.addFlagSubject.next(coords);
  }

  actualAddFlag(coords) {
    const picture = this.pictures[this.currentPicture];

    let answer = this.answer;
    if (Array.isArray(answer)) {
      answer = answer.find(a => a.id === picture.answerId);
    }

    this.addFlagModal.show({
      answer,
      picture,
      coords,
      confirmed: async () => {
        this.toastr.success('Flag Saved');
        this.placeholderFlag = undefined;
        this.updateCurrentFlags();
        Object.assign(answer, omit(await this.answerService.get(answer.id), 'pictures'));
        this.answerChange.emit(answer);
        this.updateCounter.emit();
      },
      canceled: () => {
        console.log('closed');
        this.placeholderFlag = undefined;
      },
    });
  }

  /** Keep dual-monitor / parent in sync after markup is saved on this picture. */
  onMarkupChanged(picture: any): void {
    if (!picture) {
      return;
    }
    const answer = this.getAnswerForCurrentPicture();
    if (!answer) {
      return;
    }
    const existing = (answer.pictures || []).find((p: any) => p.id === picture.id);
    if (existing) {
      existing.markup = picture.markup;
      existing.updatedAt = picture.updatedAt;
      if (picture.Location) {
        existing.Location = picture.Location;
      }
    }
    // false-path on review: merge without refreshAnswers, but push to dual monitor.
    this.answerChange.emit(answer);
  }

  ngOnInit() {
    this.setSelectedPicture();
    this.lOptions = { zoom: 15, center: latLng(32.963572, -95.302534) };
    this.queryParamsSub = this.route.queryParams.subscribe(() => {
      if (this.applyCurrentImageFromRoute()) {
        this.updateCurrentFlags();
      }
    });
  }

  ngOnDestroy() {
    this.queryParamsSub?.unsubscribe();
    this.detachBodyExitButton();
  }

  /** Select thumbnail from gallery navigation (?currentImage=pictureId). */
  private applyCurrentImageFromRoute(): boolean {
    const pid = this.route.snapshot.queryParamMap.get('currentImage');
    if (pid == null || pid === '') {
      return false;
    }
    const id = Number(pid);
    if (isNaN(id) || !this.pictures?.length) {
      return false;
    }
    const idx = this.pictures.findIndex(p => p.id === id);
    if (idx >= 0) {
      this.currentPicture = idx;
      return true;
    }
    return false;
  }

  ngOnChanges(ch) {
    this.initMap();

    if ('answer' in ch) {
      this.updateCurrentFlags();
      const curr = ch.answer.currentValue;
      const prev = ch.answer.previousValue;
      const singleIdChange = curr && prev && !Array.isArray(curr) && !Array.isArray(prev) && curr.id !== prev.id;
      const arrayIdentityChange =
        curr &&
        prev &&
        Array.isArray(curr) &&
        Array.isArray(prev) &&
        (curr.length !== prev.length || curr[0]?.id !== prev[0]?.id);
      if (singleIdChange || arrayIdentityChange) {
        setTimeout(() => {
          this.setSelectedPicture();
        });
      } else {
        setTimeout(() => {
          if (this.applyCurrentImageFromRoute()) {
            this.updateCurrentFlags();
          }
        });
      }
    }
  }

  setSelectedPicture() {
    if (this.applyCurrentImageFromRoute()) {
      return;
    }
    if (this.navigatedByArrow && this.navigatedByArrow === 'left') {
      this.currentPicture = this.pictures.length - 1;
    } else {
      this.currentPicture = 0;
    }
  }

  initMap() {
    this.markers = new FeatureGroup();

    this.lLayers = [
      tileLayer(`https://{s}.google.com/vt/lyrs=s,h,m&x={x}&y={y}&z={z}`, {
        maxZoom: 20,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        attribution: 'Google maps',
      }),
      this.markers,
    ];
  }

  async updateCurrentFlags() {
    if (!this.pictures || !this.pictures[this.currentPicture]) {
      return;
    }
    const flags = await this.pictureService.getFlags(this.pictures[this.currentPicture].id);
    this.currentFlags = flags;
    this.cdr.markForCheck();
  }

  /** Answer that owns the picture currently being viewed (handles repeat-question arrays). */
  private getAnswerForCurrentPicture(): any {
    const picture = this.pictures?.[this.currentPicture];
    if (!this.answer) {
      return null;
    }
    if (Array.isArray(this.answer)) {
      if (picture?.answerId != null) {
        return this.answer.find(a => a.id === picture.answerId) ?? null;
      }
      return this.answer[0] ?? null;
    }
    return this.answer;
  }

  private getFlagDate(flag: { date?: unknown; createdAt?: unknown; updatedAt?: unknown }): Date | null {
    // Prefer stable creation time — never flag.updatedAt (bumps on every edit/apply).
    const raw = flag?.date ?? flag?.createdAt;
    if (raw == null || raw === '') {
      return null;
    }
    const parsed = new Date(raw as string | number | Date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  get currentPictureFlagDate(): Date | null {
    const flagSources: { date?: unknown; createdAt?: unknown; updatedAt?: unknown }[] = [
      ...(this.currentFlags ?? []),
      ...(this.getAnswerForCurrentPicture()?.flags ?? []),
    ];

    const dates = flagSources.map(f => this.getFlagDate(f)).filter((d): d is Date => d != null);

    if (dates.length) {
      // Earliest flag creation time stays fixed when more flags are applied later.
      return dates.reduce((earliest, d) => (d.getTime() < earliest.getTime() ? d : earliest));
    }

    // No flags yet — use picture upload/capture time (not updatedAt, which changes on flag/markup).
    const picture = this.pictures?.[this.currentPicture];
    const raw = picture?.createdAt ?? picture?.createdDate ?? null;
    if (raw != null) {
      const parsed = new Date(raw);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return null;
  }

  toggle() {
    if (this.fullScreen) {
      this.detachBodyExitButton();
    }
    this.fullScreen = !this.fullScreen;
    this.showMapViewer = false;
    if (this.fullScreen) {
      this.attachBodyExitButton();
      this.updateCurrentFlags();
    }
    this.cdr.detectChanges();
    setTimeout(() => {
      this.centerMap();
      this.makeCurrentThumbVisible();
      this.closeFlags();
    }, 500);
  }

  /** Exit fullscreen (body-mounted back control + Escape). */
  exitFullscreen() {
    if (!this.fullScreen) {
      return;
    }
    this.detachBodyExitButton();
    this.fullScreen = false;
    this.showMapViewer = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.centerMap();
      this.makeCurrentThumbVisible();
      this.closeFlags();
    }, 500);
  }

  private attachBodyExitButton() {
    this.detachBodyExitButton();
    const btn = this.renderer.createElement('button') as HTMLButtonElement;
    this.renderer.setAttribute(btn, 'type', 'button');
    this.renderer.setAttribute(btn, 'aria-label', 'Exit fullscreen');
    const styles: Record<string, string> = {
      position: 'fixed',
      top: '8px',
      left: '48px',
      zIndex: '2147483646',
      padding: '12px 16px',
      minWidth: '48px',
      minHeight: '48px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '4px',
      background: 'transparent',
      color: '#fff',
      fontSize: '22px',
      cursor: 'pointer',
      boxSizing: 'border-box',
      textShadow: '0 0 3px #000',
    };
    Object.entries(styles).forEach(([k, v]) => this.renderer.setStyle(btn, k, v));
    const icon = this.renderer.createElement('i');
    this.renderer.addClass(icon, 'fa');
    this.renderer.addClass(icon, 'fa-arrow-left');
    this.renderer.setAttribute(icon, 'aria-hidden', 'true');
    this.renderer.appendChild(btn, icon);
    this.bodyExitUnlisten = this.renderer.listen(btn, 'click', (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.zone.run(() => this.exitFullscreen());
    });
    this.renderer.appendChild(this.document.body, btn);
    this.bodyExitButton = btn;
  }

  private detachBodyExitButton() {
    if (this.bodyExitUnlisten) {
      this.bodyExitUnlisten();
      this.bodyExitUnlisten = undefined;
    }
    if (this.bodyExitButton?.parentNode) {
      this.renderer.removeChild(this.bodyExitButton.parentNode, this.bodyExitButton);
    }
    this.bodyExitButton = null;
  }

  formattedDateTime(date) {
    if (!date) {
      return '';
    }
    const obj = new Date(date);
    if (isNaN(obj.getTime())) {
      return '';
    }
    return `${obj.getMonth() + 1}/${obj.getDate()}/${obj.getFullYear()} ${obj.getHours() < 10 ? '0' : ''}${obj.getHours()}:${obj.getMinutes() < 10 ? '0' : ''}${obj.getMinutes()}`;
  }

  currentPictureDisplayTimestamp() {
    return getPictureDisplayTimestamp(this.pictures[this.currentPicture], this.getAnswerForCurrentPicture()?.createdAt);
  }

  private mergeAnswerPictures(updatedAnswer: any): void {
    if (!updatedAnswer?.pictures) {
      return;
    }
    let answer = this.answer;
    if (Array.isArray(answer)) {
      answer = answer.find((a: any) => a.id === updatedAnswer.id) ?? answer[0];
    }
    if (!answer?.pictures) {
      return;
    }
    for (const apiPic of updatedAnswer.pictures) {
      const existing = answer.pictures.find((p: any) => p.id === apiPic.id || (p.key && p.key === apiPic.key));
      if (existing) {
        Object.assign(existing, apiPic);
      } else {
        answer.pictures.push(apiPic);
      }
    }
  }

  next() {
    if (this.hasNext()) {
      this.currentPicture++;
      this.fixScrollPosition();
      this.closeFlags();
    }
  }

  hasNext() {
    return this.currentPicture < this.pictures.length - 1;
  }

  prev() {
    if (this.hasPrev()) {
      this.currentPicture--;
      this.fixScrollPosition();
      this.closeFlags();
    }
  }

  hasPrev() {
    return this.currentPicture > 0;
  }

  fixScrollPosition() {
    const thumbWidth = 100;
    const resetAfterNThumbs = 4;
    const blockIndex = Math.floor(this.currentPicture / resetAfterNThumbs);
    this.thumbsContainer.nativeElement.scrollLeft = thumbWidth * blockIndex * resetAfterNThumbs;
  }

  onMapReady(map: Map) {
    this.map = map;
  }

  centerMap() {
    try {
      if (this.map) {
        this.map.fitBounds(this.markers.getBounds().pad(0.2));
      }
    } catch (e) {}
  }

  private point(color) {
    const myCustomColour = color;

    // tslint:disable-next-line: max-line-length
    const markerHtmlStyles = `background-color: ${myCustomColour}; width: 1.5rem; height: 1.5rem; display: block; left: -0.75rem; top: -0.75rem; position: relative; border-radius: 3rem 3rem 0; transform: rotate(45deg); border: 1px solid #FFFFFF`;

    const icon = divIcon({
      className: 'my-custom-pin',
      iconAnchor: [0, 24],
      popupAnchor: [0, -36],
      html: `<span style="${markerHtmlStyles}" />`,
    });

    return icon;
  }

  private closePopups() {
    const pops = document.getElementsByClassName('leaflet-popup-close-button');
    for (let i = 0; i < pops.length; i++) {
      (pops[i] as any).click();
    }
  }

  makeCurrentThumbVisible() {
    const thumbWidth = 140;
    const thumbHeight = 135;
    const wrapper = document.getElementById(`thumbs`);
    if (wrapper) {
      // scrollIntoView almost works... but doesn't make the div 100% visible
      wrapper.scrollTop = this.currentPicture * thumbHeight;
      wrapper.scrollLeft = this.currentPicture * thumbWidth;
    }
  }

  editFlag({ flag, clientX, clientY }) {
    const picture = this.pictures[this.currentPicture];

    let answer = this.answer;
    if (Array.isArray(answer)) {
      answer = answer.find(a => a.id === picture.answerId);
    }

    this.editFlagModal.show({
      flag,
      answer,
      coords: { clientX, clientY },
      changed: async newFlag => {
        const original = answer.flags.find(f => f.id === newFlag.id);
        original.text = newFlag.text;
        original.subtype = newFlag.subtype;
        original.subtypeId = newFlag.subtypeId;
        original.picture = newFlag.picture;
        original.pictureId = newFlag.pictureId;
        original.score = newFlag.score;
        original.groupId = newFlag.groupId;
        this.toastr.success('Flag Saved');
        this.updateCurrentFlags();
        Object.assign(answer, await this.answerService.get(answer.id));
        this.answerChange.emit(answer);
      },
      deleted: async () => {
        answer.flags.splice(
          answer.flags.findIndex(f => f.id === flag.id),
          1,
        );
        this.toastr.success('Flag Removed');
        this.updateCurrentFlags();
        Object.assign(answer, await this.answerService.get(answer.id));
        this.answerChange.emit(answer);
        this.updateCounter.emit();
      },
    });
  }

  async moveFlag({ flag, x, y }) {
    const picture = this.pictures[this.currentPicture];

    let answer = this.answer;
    if (Array.isArray(answer)) {
      answer = answer.find(a => a.id === picture.answerId);
    }

    const newFlag = (answer.flags || []).find(f => f.id === flag.id);
    const original = answer.flags.find(f => f.id === newFlag.id);
    original.x = x;
    original.y = y;

    await this.flagService.edit(flag.id, { ...flag, x, y });
    this.toastr.success('Flag Saved');
    await this.updateCurrentFlags();
    Object.assign(answer, await this.answerService.get(answer.id));
    this.answerChange.emit(answer);
    // Remove moved flag
    if (this.flagsWithTooltip.length > 0) {
      this.flagsWithTooltip = this.flagsWithTooltip.filter(f => f !== flag.id);
    }
  }

  openPictureFlag(flag) {
    const pictureIndex = this.pictures.findIndex(pic => pic.id === flag.pictureId);
    this.currentPicture = pictureIndex;
    if (this.currentPicture === pictureIndex) {
      return;
    }
    // Make sure correct picture is shown
    setTimeout(() => {
      this.viewerSmall.first.openFlagById(flag.id);
    }, 200);
  }

  attachFlagToPicture(flag) {
    return new Promise<{ changes: AnswerChange[]; updatedFlag: Flag }>(res => {
      this.zone.run(async () => {
        const coord = this.viewerSmall.first.lastMouseUp;

        if (coord) {
          flag.picture = this.pictures[this.currentPicture];
          flag.pictureId = flag.picture.id;
          flag.x = coord.x;
          flag.y = coord.y;

          this.currentFlags = [...this.currentFlags, flag];

          const savedAnswer = await this.flagService.edit(flag.id, { ...flag, x: coord.x, y: coord.y });
          await this.updateCurrentFlags();
          const updatedFlag = this.currentFlags.find(a => a.id === flag.id);
          this.toastr.success('Flag Saved');
          res({
            changes: savedAnswer.changes,
            updatedFlag: updatedFlag,
          });
        }
      });
    });
  }

  async attachFlagToPictureFull(flag) {
    const coord = this.viewerFull.first.imgSize;

    if (coord) {
      const picture = clone(this.pictures[this.currentPicture]);
      delete picture.answer;

      flag.picture = picture;
      flag.pictureId = flag.picture.id;
      flag.x = coord[0] / 2;
      flag.y = coord[1] / 2;

      this.flagsWithTooltip.push(flag.id);
      this.currentFlags = [...this.currentFlags, flag];

      await this.flagService.edit(flag.id, { ...flag });
      this.toastr.success('Flag Saved');
      this.updateCurrentFlags();
    }
  }

  getLinkParams(picture) {
    if (picture) {
      const [item, section, question] = picture.deeplink;
      return {
        item,
        section,
        question,
        right: 'question',
        currentImage: undefined,
      };
    }
  }

  unpinnedFlagsCount() {
    const answer = this.answer;
    if (!answer) {
      return 0;
    }
    if (Array.isArray(answer)) {
      return answer.reduce(
        (n, a: { flags?: { pictureId?: number }[] }) => n + (a.flags || []).filter(f => !f.pictureId).length,
        0,
      );
    }
    return (answer.flags || []).filter(flag => !flag.pictureId).length;
  }

  get answerFlagsDisplayCount(): number {
    const pictureAnswer = this.getAnswerForCurrentPicture();
    if (pictureAnswer?.flags?.length != null) {
      return pictureAnswer.flags.length;
    }

    if (this.currentFlags?.length) {
      return this.currentFlags.length;
    }

    const a = this.answer;
    if (!a) {
      return 0;
    }
    if (Array.isArray(a)) {
      return a.reduce((n, x: { flags?: unknown[] }) => n + (x.flags?.length ?? 0), 0);
    }
    return a.flags?.length ?? 0;
  }

  toggleFlags() {
    this.showFlags = !this.showFlags;
    this.updateImage += 1;
  }

  closeFlags() {
    this.showFlags = null;
    this.iconStyle = '0';
    this.updateImage += 1;
  }

  setFlexValues() {
    const minFlexValue = 6.5;
    const minRightColumnFlexValue = 3.5;

    const maxFlexValue = 8.3;
    const maxRightColumnFlexValue = 1.7;

    const minWidth = 1300;
    const maxWidth = 2800;

    if (window.innerWidth > maxWidth) {
      this.flexValue = maxFlexValue.toString();
      this.rightColumnFlexValue = maxRightColumnFlexValue.toString();
    } else if (window.innerWidth < minWidth) {
      this.flexValue = minFlexValue.toString();
      this.rightColumnFlexValue = minRightColumnFlexValue.toString();
    } else {
      const t = (window.innerWidth - minWidth) / (maxWidth - minWidth);
      this.flexValue = (minFlexValue + t * (maxFlexValue - minFlexValue)).toString();
      this.rightColumnFlexValue = (
        minRightColumnFlexValue +
        t * (maxRightColumnFlexValue - minRightColumnFlexValue)
      ).toString();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.target === document.body) {
      switch (event.key) {
        case 'ArrowLeft':
          this.prev();
          break;
        case 'ArrowRight':
          this.next();
          break;
        case 'Escape':
          this.closeFlags();
          break;
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(_event: KeyboardEvent) {
    if (this.fullScreen) {
      this.exitFullscreen();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setFlexValues();
  }

  addImage() {
    let answer = this.answer;
    if (Array.isArray(answer)) {
      answer = answer[0];
    }

    this.uploadImageModal.show({
      answer,
      finished: updatedAnswer => {
        const prevCount = this.pictures.length;
        this.mergeAnswerPictures(updatedAnswer);
        this.toastr.success('Images Saved');
        this.answer.changes = updatedAnswer.changes;
        if (this.pictures.length > prevCount) {
          this.currentPicture = this.pictures.length - 1;
        }
        this.answerChange.emit(this.answer);
        this.updateCounter.emit();
      },
    });
  }

  async removeImage(picture) {
    const flags = await this.pictureService.getFlags(picture.id);
    const hasFlags = flags.length > 0;

    const remove = (unlinkFlags: boolean) => {
      this.removeImageApi(picture, unlinkFlags);
    };

    if (hasFlags) {
      this.dialog.confirm().show({
        message: `Are you sure you want to remove this image? All associated flags will be unlinked.`,
        confirmed: () => this.removeImageApi(picture, true),
      });
    } else {
      this.dialog.confirm().show({
        message: `Are you sure you want to remove this image?`,
        confirmed: () => this.removeImageApi(picture, false),
      });
    }
  }

  async removeImageApi(picture, flag) {
    let answer = this.answer;
    if (Array.isArray(answer)) {
      answer = answer.find(a => a.id === picture.answerId) ?? answer[0];
    }

    answer.pictures.splice(
      answer.pictures.findIndex(p => p.id === picture.id),
      1,
    );

    if (this.currentPicture === this.pictures.length) {
      this.currentPicture--;
    }

    if (flag) {
      await this.pictureService.removePictureWithFlags(picture.id);
      this.flagChange.emit(true);
    } else {
      await this.pictureService.remove(picture.id);
    }
    this.answerChange.emit(this.answer);
    this.updateCounter.emit();
    this.toastr.success('Image Deleted');

    if (answer?.id != null) {
      answer.changes = await this.answerService.getChanges(answer.id);
    }
  }
}
